/* swt-header.js · SWT Header v2.2 core (Concept-style motion). Vanilla JS.
   v2.1: one sliding pill (glider) follows hover / focus / the open menu;
   a soft light follows the cursor over the glass header.
   v2.2: one glass surface. Opening any menu sets --h-stretch on the header
   (extra height the menu needs) so the header glass itself grows down to
   hold it. --h-navb = bottom of the nav row (where menus start). Small menus
   get --dx so their list sits under their own nav item. --swt-header-h is
   always published (the page is pulled up behind the glass by that amount).
   v2.3: only part of the bar stretches. --h-clip cuts the glass into the bar
   plus a tongue under the open menu's box (clip-path: path(), same commands
   open and closed so it animates): 16px water-drop curves where the tongue
   leaves the bar, rounded bottom corners. Switching menus morphs the tongue
   across; closing pulls it back up, then the flat closed clip stays on
   (v2.9.4, desktop only).
   Menus: 120ms hover intent, instant switch between open panels, 180ms
   close grace, tap-to-open on touch, Esc + arrow keys, closes on outside
   click, dim click or 60px of scroll. Sticky: red bar scrolls away, card
   sticks, slims after 160px, un-slims under 60px. Account dropdown.
   Extra modules (countdown, search, cart, mobile menu) register on
   window.SWTHeaderModules from swt-header-extras.js / swt-header-drawer.js.
   v2.4: while a menu is open, floating app widgets sit under the page dim
   (html.swt-under-dim, styles in swt-header-menu.css). */
(function () {
  'use strict';
  var OPEN_DELAY = 120, CLOSE_DELAY = 180, SLIM_ON = 160, SLIM_OFF = 60, SCROLL_CLOSE = 60;
  var instances = {};
  var mqDesktop = window.matchMedia('(min-width: 990px)');

  function init(section) {
    var root = section.querySelector('[data-swt-header]');
    if (!root) return null;
    var doc = document;
    var ac = new AbortController();
    var sig = { signal: ac.signal };
    var intervals = [], cleanups = [], observers = [];
    var dim = section.querySelector('[data-swt-dim]');
    var topbar = section.querySelector('[data-swt-topbar]');

    /* ---------- menus ---------- */
    var items = Array.prototype.filter.call(root.querySelectorAll('[data-swt-item]'), function (li) {
      return li.getAttribute('data-swt-item') !== 'link';
    });
    var openItem = null, openT = 0, closeT = 0, lastPointer = 'mouse', openScrollY = 0;

    function kindOf(li) { return li.getAttribute('data-swt-item'); }
    function toggleOf(li) { return li.querySelector('[data-swt-toggle]'); }
    function panelOf(li) { return li.querySelector('[data-swt-panel]'); }

    function lazy(li) {
      var p = panelOf(li);
      if (!p || p.getAttribute('data-swt-loaded')) return;
      p.setAttribute('data-swt-loaded', '1');
      p.querySelectorAll('img[data-swt-src]').forEach(function (img) {
        img.src = img.getAttribute('data-swt-src');
        img.removeAttribute('data-swt-src');
      });
      li.dispatchEvent(new CustomEvent('swt:menu:firstopen', { bubbles: true }));
    }

    function setState(li, open) {
      li.classList.toggle('is-open', open);
      var b = toggleOf(li);
      if (b) b.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function syncChrome() {
      section.classList.toggle('has-mega', !!openItem);
      root.classList.toggle('has-open', !!openItem);
      underDim(!!openItem);
      stretch();
    }

    /* floating app widgets (wishlist tab, reviews + chat buttons, Google store
       badge) go under the dim while a menu is open. The class stays until the
       dim has faded out, then goes, so they come back exactly as they were. */
    var underT = 0;
    function underDim(on) {
      var html = doc.documentElement;
      clearTimeout(underT);
      if (on) { html.classList.add('swt-under-dim'); return; }
      if (!html.classList.contains('swt-under-dim')) return;
      var ms = dim ? (parseFloat(window.getComputedStyle(dim).transitionDuration) || 0) * 1000 : 0;
      underT = setTimeout(function () { html.classList.remove('swt-under-dim'); }, ms);
    }
    cleanups.push(function () { clearTimeout(underT); doc.documentElement.classList.remove('swt-under-dim'); });

    /* ---------- stretch: a glass tongue grows out of the bar under the open menu ---------- */
    var navRow = root.querySelector('.swt-header__navrow');
    var JOIN = 16, R_DROP = 20, R_MEGA = 26, EDGE = 16;
    var shaped = false, tx1 = 0, tx2 = 0, unshapeT = 0;
    /* v2.9.4 (10-13-26) flicker fix: the glass box is never animated (see
       swt-header.css). It grows at once, behind the animating clip-path, and
       shrinks only once the shape animation is over (400ms, before the 420ms
       unshape below), so the clip never shows a box that is too short. */
    var stretchNow = 0, shrinkT = 0;
    function setStretch(px) {
      clearTimeout(shrinkT);
      if (px >= stretchNow) { stretchNow = px; root.style.setProperty('--h-stretch', px + 'px'); return; }
      shrinkT = setTimeout(function () { stretchNow = px; root.style.setProperty('--h-stretch', px + 'px'); }, 400);
    }

    function placeDrop(li, p) {
      var list = p.querySelector('.swt-drop__list');
      if (!list) return;
      var rr = root.getBoundingClientRect(), lr = li.getBoundingClientRect();
      var max = rr.width - list.offsetWidth - EDGE;
      p.style.setProperty('--dx', Math.round(Math.max(EDGE, Math.min(lr.left - rr.left - 8, max))) + 'px');
    }
    function r1(n) { return Math.round(n * 10) / 10; }
    /* bar from the top down to barB, tongue x1..x2 from barB down to y2.
       Same commands for every state so the browser can morph between them. */
    function pathFor(w, barB, x1, x2, y2, rb, rj) {
      return "path('M0 0 H" + r1(w) + ' V' + r1(barB) +
        ' H' + r1(x2 + rj) + ' A' + rj + ' ' + rj + ' 0 0 0 ' + r1(x2) + ' ' + r1(barB + rj) +
        ' V' + r1(y2 - rb) + ' A' + rb + ' ' + rb + ' 0 0 1 ' + r1(x2 - rb) + ' ' + r1(y2) +
        ' H' + r1(x1 + rb) + ' A' + rb + ' ' + rb + ' 0 0 1 ' + r1(x1) + ' ' + r1(y2 - rb) +
        ' V' + r1(barB + rj) + ' A' + rj + ' ' + rj + ' 0 0 0 ' + r1(x1 - rj) + ' ' + r1(barB) +
        " H0 Z')";
    }
    function closedPath() {
      var h = root.offsetHeight;
      /* v2.9.4: 0.1px join (was 0) keeps the flat shape from being a plain
         rectangle, which Chrome would swap for a cheap rect clip; then the clip
         mask would still be built on hover. Invisible at 0.1px. */
      return pathFor(root.offsetWidth, h, tx1, tx2, h, 0, 0.1);
    }
    function setShapeNow(path) {
      root.classList.add('is-reshaping');
      root.style.setProperty('--h-clip', path);
      void window.getComputedStyle(root, '::before').clipPath;
      void root.offsetHeight;
      root.classList.remove('is-reshaping');
    }
    function stretch() {
      clearTimeout(unshapeT);
      var p = (openItem && mqDesktop.matches) ? panelOf(openItem) : null;
      var box = p && (p.querySelector('.swt-drop__list') || p.querySelector('.swt-mega__inner'));
      if (!box) {
        setStretch(0);
        if (shaped) {
          root.style.setProperty('--h-clip', closedPath());
          unshapeT = setTimeout(function () {
            if (openItem) return;
            shaped = false;
            /* v2.9.4: the clip stays on (flat closed shape), see measure() */
            setShapeNow(closedPath());
          }, 420);
        }
        return;
      }
      if (kindOf(openItem) === 'drop') placeDrop(openItem, p);
      var w = root.offsetWidth, h = root.offsetHeight;
      var x1 = Math.max(JOIN, p.offsetLeft + box.offsetLeft);
      var x2 = Math.min(w - JOIN, p.offsetLeft + box.offsetLeft + box.offsetWidth);
      var y2 = p.offsetTop + box.offsetTop + box.offsetHeight;
      var barB = navRow ? navRow.offsetTop + navRow.offsetHeight : h;
      var room = Math.max(0, (y2 - barB) / 2);
      var rb = Math.min(kindOf(openItem) === 'mega' ? R_MEGA : R_DROP, room);
      var rj = Math.min(JOIN, room);
      if (!shaped) {
        /* first open: start from a flat tongue right under the menu, then grow */
        tx1 = x1; tx2 = x2;
        setShapeNow(closedPath());
        shaped = true;
      }
      tx1 = x1; tx2 = x2;
      setStretch(Math.max(0, Math.round(y2 - h)));
      root.style.setProperty('--h-clip', pathFor(w, barB, x1, x2, y2, rb, rj));
    }

    /* ---------- sliding pill (glider) ---------- */
    var navList = root.querySelector('.swt-nav');
    var glider = null, lit = null;
    if (navList) {
      glider = navList.querySelector('[data-swt-glider]');
      if (!glider) {
        glider = doc.createElement('li');
        glider.className = 'swt-nav__glider';
        glider.setAttribute('aria-hidden', 'true');
        glider.setAttribute('data-swt-glider', '');
        navList.insertBefore(glider, navList.firstChild);
      }
      navList.classList.add('has-glider');
    }

    function placeGlider(li) {
      var lr = li.getBoundingClientRect(), ur = root.getBoundingClientRect();
      glider.style.setProperty('--gx', (lr.left - ur.left) + 'px');
      glider.style.setProperty('--gy', (lr.top - ur.top) + 'px');
      glider.style.setProperty('--gw', lr.width + 'px');
      glider.style.setProperty('--gh', lr.height + 'px');
    }

    function light(li) {
      if (!glider) return;
      if (li && !mqDesktop.matches) li = null;
      if (li === lit) return;
      var wasOff = !lit;
      if (lit) lit.classList.remove('is-lit');
      lit = li;
      if (!li) { navList.classList.remove('is-gliding'); return; }
      li.classList.add('is-lit');
      if (wasOff) navList.classList.add('is-snap');
      placeGlider(li);
      if (wasOff) { void glider.offsetWidth; navList.classList.remove('is-snap'); }
      navList.classList.add('is-gliding');
    }

    function openMenu(li) {
      clearTimeout(openT); clearTimeout(closeT);
      if (openItem === li) return;
      var switching = !!openItem;
      if (switching) root.classList.add('is-switching');
      if (openItem) setState(openItem, false);
      openItem = li;
      openScrollY = window.scrollY;
      setState(li, true);
      lazy(li);
      syncChrome();
      light(li);
      if (switching) {
        void root.offsetHeight;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { root.classList.remove('is-switching'); });
        });
      }
    }

    function closeMenu(li, focusToggle) {
      clearTimeout(openT); clearTimeout(closeT);
      li = li || openItem;
      if (!li) return;
      setState(li, false);
      if (openItem === li) openItem = null;
      syncChrome();
      if (glider && (lastPointer === 'touch' || !navList.matches(':hover'))) light(null);
      if (focusToggle) { var b = toggleOf(li); if (b) b.focus(); }
    }

    function focusFirst(li) {
      var p = panelOf(li);
      if (!p) return;
      setTimeout(function () {
        var f = p.querySelector('a[href], button:not([disabled])');
        if (f) f.focus();
      }, 60);
    }

    root.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType || 'mouse'; }, sig);

    items.forEach(function (li) {
      var link = li.querySelector('.swt-nav__link');
      var btn = toggleOf(li);
      var panel = panelOf(li);

      li.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'touch' || !mqDesktop.matches) return;
        clearTimeout(closeT);
        if (openItem === li) return;
        clearTimeout(openT);
        if (openItem) { openMenu(li); return; }
        openT = setTimeout(function () { openMenu(li); }, OPEN_DELAY);
      }, sig);

      li.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'touch') return;
        clearTimeout(openT);
        if (openItem === li) {
          clearTimeout(closeT);
          closeT = setTimeout(function () { closeMenu(li); }, CLOSE_DELAY);
        }
      }, sig);

      if (link) {
        link.addEventListener('click', function (e) {
          if (lastPointer === 'touch' && openItem !== li) { e.preventDefault(); openMenu(li); }
        }, sig);
        link.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') { e.preventDefault(); openMenu(li); focusFirst(li); }
        }, sig);
      }

      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (openItem === li) closeMenu(li);
          else { openMenu(li); if (e.detail === 0) focusFirst(li); }
        }, sig);
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') { e.preventDefault(); openMenu(li); focusFirst(li); }
        }, sig);
      }

      li.addEventListener('focusout', function (e) {
        if (openItem !== li) return;
        var to = e.relatedTarget;
        if (to && li.contains(to)) return;
        if (li.matches(':hover')) return;
        closeMenu(li);
      }, sig);

      if (panel) panel.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        var links = Array.prototype.slice.call(panel.querySelectorAll('a[href]'));
        var i = links.indexOf(doc.activeElement);
        if (i < 0) return;
        e.preventDefault();
        links[e.key === 'ArrowDown' ? Math.min(i + 1, links.length - 1) : Math.max(i - 1, 0)].focus();
      }, sig);
    });

    if (dim) dim.addEventListener('click', function () { closeMenu(); }, sig);
    doc.addEventListener('click', function (e) {
      if (openItem && !openItem.contains(e.target)) closeMenu();
    }, sig);
    mqDesktop.addEventListener('change', function () { closeMenu(); light(null); }, sig);

    /* glider follows hover and keyboard focus over every nav item */
    if (glider) {
      Array.prototype.forEach.call(navList.querySelectorAll('.swt-nav__item'), function (li) {
        li.addEventListener('pointerenter', function (e) {
          if (e.pointerType === 'touch') return;
          light(li);
        }, sig);
      });
      navList.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'touch') return;
        light(openItem);
      }, sig);
      navList.addEventListener('focusin', function (e) {
        var li = e.target.closest ? e.target.closest('.swt-nav__item') : null;
        if (li) light(li);
      }, sig);
      navList.addEventListener('focusout', function (e) {
        if (e.relatedTarget && navList.contains(e.relatedTarget)) return;
        if (!navList.matches(':hover')) light(openItem);
      }, sig);
    }

    /* ---------- liquid light: soft glow follows the cursor (desktop, motion allowed) ---------- */
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    var lessMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var shineRaf = 0, px = 0, py = 0;
    root.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse' || !finePointer.matches || lessMotion.matches) return;
      root.classList.add('is-shining');
    }, sig);
    root.addEventListener('pointerleave', function () { root.classList.remove('is-shining'); }, sig);
    root.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse' || !finePointer.matches || lessMotion.matches) return;
      px = e.clientX; py = e.clientY;
      if (shineRaf) return;
      shineRaf = requestAnimationFrame(function () {
        shineRaf = 0;
        var r = root.getBoundingClientRect();
        root.style.setProperty('--mx', (px - r.left) + 'px');
        root.style.setProperty('--my', (py - r.top) + 'px');
      });
    }, sig);
    cleanups.push(function () { if (shineRaf) cancelAnimationFrame(shineRaf); });

    /* ---------- sticky + slim ---------- */
    var sticky = root.getAttribute('data-sticky') === 'true';
    var slim = false, stuck = false, ticking = false;

    function onScroll() {
      ticking = false;
      var y = window.scrollY;
      if (sticky) {
        var nowStuck = y > 0 && root.getBoundingClientRect().top <= 0.5;
        if (nowStuck !== stuck) { stuck = nowStuck; root.classList.toggle('is-stuck', stuck); section.classList.toggle('is-stuck', stuck); }
        if (!slim && y > SLIM_ON) { slim = true; root.classList.add('is-slim'); }
        else if (slim && y < SLIM_OFF) { slim = false; root.classList.remove('is-slim'); }
      }
      if (openItem && Math.abs(y - openScrollY) > SCROLL_CLOSE) closeMenu();
    }

    function measure() {
      doc.documentElement.style.setProperty('--swt-header-h', root.offsetHeight + 'px');
      if (navRow) root.style.setProperty('--h-navb', (navRow.offsetTop + navRow.offsetHeight) + 'px');
      if (lit) placeGlider(lit);
      if (openItem) stretch();
      /* v2.9.4 (10-13-26) flicker fix: on desktop the glass always carries its
         clip-path (the flat closed shape while no menu is open), so Chrome never
         has to build the clip mask in the first frames of a hover. It was added
         on open and removed 420ms after close, i.e. rebuilt on every hover.
         Phone / tablet (no menus on the bar): no clip, as before. */
      else if (!shaped) {
        if (mqDesktop.matches) setShapeNow(closedPath());
        else root.style.removeProperty('--h-clip');
      }
      if (!sticky) { section.classList.add('is-static'); section.style.top = ''; return; }
      section.classList.remove('is-static');
      var off = root.offsetTop;
      section.style.top = (off > 0 ? -off : 0) + 'px';
      onScroll();
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true, signal: ac.signal });
    window.addEventListener('resize', measure, sig);
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(measure);
      ro.observe(root);
      if (topbar) ro.observe(topbar);
      items.forEach(function (li) { var p = panelOf(li); if (p) ro.observe(p); });
      observers.push(ro);
    }
    measure();

    /* ---------- account dropdown ---------- */
    var acct = root.querySelector('[data-swt-account]');
    if (acct) doc.addEventListener('click', function (e) {
      if (acct.hasAttribute('open') && !acct.contains(e.target)) acct.removeAttribute('open');
    }, sig);

    /* ---------- mobile drawer: behaviour lives in swt-header-drawer.js ---------- */
    var drawerApi = { open: function () {}, close: function () {} };
    function closeDrawer() { drawerApi.close(); }

    /* ---------- Esc: open menu first, then drawer / account ---------- */
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (openItem) { closeMenu(openItem, true); e.swtHandled = true; return; }
      closeDrawer();
      if (acct) acct.removeAttribute('open');
    }, sig);

    /* ---------- extra modules ---------- */
    var ctx = { section: section, root: root, doc: doc, sig: sig, intervals: intervals, drawer: drawerApi, closeDrawer: closeDrawer, closeMenu: closeMenu };
    (window.SWTHeaderModules || []).forEach(function (mod) {
      try { var c = mod(ctx); if (typeof c === 'function') cleanups.push(c); } catch (err) { if (window.console) console.error('[swt-header]', err); }
    });

    return {
      destroy: function () {
        ac.abort();
        intervals.forEach(clearInterval);
        observers.forEach(function (o) { o.disconnect(); });
        cleanups.forEach(function (c) { c(); });
        clearTimeout(openT); clearTimeout(closeT);
      }
    };
  }

  function mount(section) {
    if (!section || !section.id) return;
    if (instances[section.id]) instances[section.id].destroy();
    var inst = init(section);
    if (inst) instances[section.id] = inst;
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target;
    if (s && s.classList && s.classList.contains('swt-header-section')) mount(s);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var s = e.target;
    if (s && instances[s.id]) { instances[s.id].destroy(); delete instances[s.id]; }
  });

  function boot() { document.querySelectorAll('.swt-header-section').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();